import type { FirstPaymentDefinitionsDTO } from '@/shared/model';
import { isPrepaymentDefinition } from './getPrepaymentSummary';
import { toPrepaymentPercent } from './toPrepaymentPercent';
import {
  type PrepaymentDeadline,
  PrepaymentDeadlineKind,
  type PrepaymentOperator,
  type PrepaymentOption,
  type PrepaymentSource,
} from '../model/types';

/**
 * Варианты предоплаты, сгруппированные по туроператорам, — блоки шторки «Предоплата».
 * Один туроператор обычно встречается у нескольких продуктов карточки с одними и теми же
 * условиями, поэтому варианты дедуплицируются по доле.
 */
export function getPrepaymentOperators(products: readonly PrepaymentSource[]): PrepaymentOperator[] {
  // Map хранит порядок вставки — операторы идут в порядке первого появления среди продуктов.
  const operators = new Map<number, PrepaymentOperator>();

  for (const product of products) {
    const { operator } = product;

    if (!operator) continue;

    const options = (product.firstPaymentDefinitions ?? [])
      .filter((definition) => isPrepaymentDefinition(definition))
      .map((definition) => toPrepaymentOption(definition));

    if (!options.length) continue;

    const group = operators.get(operator.id);

    if (group) {
      group.options.push(...options);
      continue;
    }

    operators.set(operator.id, { id: operator.id, name: operator.nameRu, logo: operator.cover, options });
  }

  return [...operators.values()].map((operator) => ({ ...operator, options: normalizeOptions(operator.options) }));
}

function toPrepaymentOption(definition: FirstPaymentDefinitionsDTO): PrepaymentOption {
  return {
    percent: toPrepaymentPercent(definition.firstPaymentPart),
    deadline: toPrepaymentDeadline(definition),
  };
}

function toPrepaymentDeadline(definition: FirstPaymentDefinitionsDTO): PrepaymentDeadline | null {
  const daysAfterReservation = definition.daysAfterReservationForFullPayment;

  if (isDeadlineDays(daysAfterReservation)) {
    return { kind: PrepaymentDeadlineKind.AfterReservation, days: daysAfterReservation };
  }

  const daysBeforeCheckIn = definition.daysBeforeCheckInDateForFullPayment;

  if (isDeadlineDays(daysBeforeCheckIn)) {
    return { kind: PrepaymentDeadlineKind.BeforeCheckIn, days: daysBeforeCheckIn };
  }

  return null;
}

// Срок полной оплаты приходит null или нулём, когда туроператор его не назвал.
function isDeadlineDays(days: number | null): days is number {
  return typeof days === 'number' && Number.isFinite(days) && days > 0;
}

function normalizeOptions(options: readonly PrepaymentOption[]): PrepaymentOption[] {
  const byPercent = new Map<number, PrepaymentOption>();

  for (const option of options) {
    if (!byPercent.has(option.percent)) byPercent.set(option.percent, option);
  }

  // В макете сначала идёт крупная доля: 50%, потом 10%.
  return [...byPercent.values()].sort((first, second) => second.percent - first.percent);
}
